import isEqual from "fast-deep-equal/es6";
import { useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { MoreToggle } from "../../components/MoreToggle";
import { cubeGeometry } from "../../state/cubeGeometry";
import {
  useOmgeving,
  type Analyse,
  type Omgeving as OmgevingData,
  type OmgevingDocument,
  type Regeling,
} from "../../state/omgeving";
import { Cube } from "../../state/types";
import {
  analyseTekst,
  artikelLabel,
  kaartLink,
  regelingRegel,
  sorteerRegelingen,
} from "./omgevingFormat";
import { A, Info, KV, SubHeader } from "./util";

const TOON = 5;

type Publiek = { url?: string | null; kaartUrl?: string | null };

/** Titel naar de tekst, en Regels op de kaart ernaast als die ergens anders heen gaat. */
const Titel = ({ item, children }: { item: Publiek; children: React.ReactNode }) => {
  const kaart = kaartLink(item);
  return (
    <>
      {item.url ? <A href={item.url}>{children}</A> : children}
      {kaart && (
        <>
          {" "}
          <A href={kaart}>kaart</A>
        </>
      )}
    </>
  );
};

const Klein = ({ children }: { children: React.ReactNode }) => (
  <div className="text-sm text-gray-600">{children}</div>
);

const Lijst = <T,>({ kop, rijen, Rij }: { kop: string; rijen: T[]; Rij: React.FC<{ r: T }> }) => {
  const [open, setOpen] = useState(false);
  if (!rijen.length) return null;
  return (
    <KV k={`${kop} (${rijen.length})`}>
      {(open ? rijen : rijen.slice(0, TOON)).map((r, i) => (
        <Rij key={i} r={r} />
      ))}
      {rijen.length > TOON && <MoreToggle open={open} setOpen={setOpen} />}
    </KV>
  );
};

const RegelingRij = ({ r }: { r: Regeling }) => (
  <div className="py-1">
    <Titel item={r}>{r.titel}</Titel>
    <Klein>{regelingRegel(r)}</Klein>
  </div>
);

const DocumentRij = ({ r }: { r: OmgevingDocument }) => (
  <div className="py-1">
    <Titel item={r}>{r.titel}</Titel>
    {r.soort && <Klein>{r.soort}</Klein>}
  </div>
);

const AnalyseRegel = ({ a }: { a: Analyse }) => {
  const tekst = analyseTekst(a.analyse, a.resultaat);
  if (!tekst) return null;
  const artikel = artikelLabel(a.artikel);
  return (
    <div className="py-1">
      {a.analyse}: {tekst}
      {(a.regeling || artikel) && (
        <Klein>
          {a.url ? (
            <A href={a.url}>{[a.regeling, artikel].filter(Boolean).join(", ")}</A>
          ) : (
            [a.regeling, artikel].filter(Boolean).join(", ")
          )}
        </Klein>
      )}
    </div>
  );
};

const Inhoud = ({ omgeving }: { omgeving: OmgevingData }) => {
  const regelingen = sorteerRegelingen(omgeving.regelingen ?? []);
  const documenten = omgeving.documenten ?? [];
  const analyses = omgeving.analyses ?? [];
  const plannen = omgeving.imro?.plannen ?? [];
  return (
    <>
      {(regelingen.length > 0 || documenten.length > 0) && (
        <>
          <SubHeader>Regelingen en beleid (DSO)</SubHeader>
          <Info as="dl">
            <Lijst kop="Geldende regelingen" rijen={regelingen} Rij={RegelingRij} />
            <Lijst kop="Plannen en visies" rijen={documenten} Rij={DocumentRij} />
          </Info>
        </>
      )}
      {(analyses.length > 0 || plannen.length > 0) && (
        <>
          <SubHeader>Regels op deze locatie</SubHeader>
          <Info as="dl">
            {analyses.length > 0 && (
              <KV k="Uit de regelteksten">
                {analyses.map((a, i) => (
                  <AnalyseRegel key={i} a={a} />
                ))}
              </KV>
            )}
            {/* Bestemming en bouwvlak staan al hierboven, uit de cube */}
            {plannen.map((p, i) => (
              <KV key={i} k={p.naam}>
                <Titel item={p}>{[p.soort, p.status].filter(Boolean).join(", ")}</Titel>
                {p.aanduidingen.length > 0 && <div>Aanduidingen: {p.aanduidingen.join("; ")}</div>}
                {p.gebiedsaanduidingen.length > 0 && (
                  <div>Gebiedsaanduidingen: {p.gebiedsaanduidingen.join("; ")}</div>
                )}
                {p.maatvoeringen.length > 0 && <div>Maatvoering: {p.maatvoeringen.join("; ")}</div>}
                {p.analyses.map((a, j) => (
                  <AnalyseRegel key={j} a={a} />
                ))}
              </KV>
            ))}
          </Info>
        </>
      )}
    </>
  );
};

const leeg = (o?: OmgevingData) =>
  !o?.regelingen?.length && !o?.documenten?.length && !o?.imro?.plannen?.length;

/**
 * Wat het Omgevingsloket over deze plek weet, via de omgevingsmonitor:
 * regelingen, plannen en visies, en de regels die daaruit op de locatie
 * vallen. Bestemming en bouwvlak niet — die staan al in deze sectie uit de
 * cube, en dezelfde bestemming tweemaal laat de lezer raden welke telt.
 *
 * Dichtgeklapt tot de lezer erom vraagt: een vers gebied kost de monitor tot
 * 90 seconden. De regel staat er dus ook als er niets blijkt te zijn — dat is
 * de prijs van lui laden.
 */
export const Omgeving = ({ cube }: { cube: Cube }) => {
  const geometry = useMemo(() => cube?.geometry && cubeGeometry(cube), [cube]);
  // Open voor díé geometrie: klik je naar een ander perceel, dan klapt hij
  // dicht in plaats van meteen de volgende trage vraag af te vuren
  const [openVoor, setOpenVoor] = useState<GeoJSON.Geometry>();
  const open = !!geometry && isEqual(openVoor, geometry);
  const [opgehaaldVoor, state, omgeving, load] = useOmgeving(
    useShallow((s) => [s.geometry, s.state, s.omgeving, s.load])
  );

  useEffect(() => {
    if (open) load(geometry);
  }, [open, geometry, load]);

  if (!geometry) return null;
  // De store kan nog het antwoord voor het vorige perceel dragen
  const actueel = isEqual(opgehaaldVoor, geometry);
  const klaar = actueel && (state === "fetched" || state === "error");
  return (
    <>
      <SubHeader>Omgevingsloket</SubHeader>
      <Info as="dl">
        <KV k="Regelingen, plannen en regels">
          <MoreToggle open={open} setOpen={(o: boolean) => setOpenVoor(o ? geometry : undefined)} />
          {open && state === "fetching" && (
            <Klein>Ophalen bij het Omgevingsloket; dit kan een minuut duren…</Klein>
          )}
          {/* Een storing is stilte, net als bij de bekendmakingen */}
          {open && klaar && leeg(omgeving) && <Klein>Geen omgevingsinformatie gevonden.</Klein>}
        </KV>
      </Info>
      {open && klaar && omgeving && <Inhoud omgeving={omgeving} />}
    </>
  );
};
